/**
 * workdayBus.js
 *
 * Tiny pub/sub that carries "open the workday reminder" from places without React context (the notification tap
 * responder, the push navigation helper) to the {@link module:helpers/WorkdayContext} provider. A request that arrives
 * before the provider subscribed (cold start from a tap) is latched and replayed on subscribe.
 *
 * @module helpers/workdayBus
 */

/** Notification `data.type` of the reminder (local notification and server push). */
export const WORKDAY_REMINDER_TYPE = 'workday_reminder';

/** Deep link the server push carries. */
export const WORKDAY_REMINDER_DEEP_LINK = 'happyapp://workday/reminder';

const listeners = new Set();
let pending = false;

/**
 * Whether a notification payload is the workday reminder (local or server push).
 * @param {object|null|undefined} data notification `data`.
 * @returns {boolean} true for the reminder.
 */
export const isWorkdayReminderPayload = (data) => {
    if (!data || typeof data !== 'object') {
        return false;
    }

    const link = String(data.deepLink || data.deeplink || data.clickAction || '').toLowerCase();

    return String(data.type || '').toLowerCase() === WORKDAY_REMINDER_TYPE || link.startsWith('happyapp://workday');
};

/**
 * Ask the provider to open the reminder modal (latched until a subscriber exists).
 * @returns {void}
 */
export const requestWorkdayReminder = () => {
    if (!listeners.size) {
        pending = true;

        return;
    }

    listeners.forEach((fn) => {
        try {
            fn();
        } catch (_e) {
            // a faulty listener must not break the others
        }
    });
};

/**
 * Subscribe to reminder-open requests; replays a latched one immediately.
 * @param {() => void} listener callback.
 * @returns {() => void} unsubscribe.
 */
export const subscribeWorkdayReminder = (listener) => {
    listeners.add(listener);

    if (pending) {
        pending = false;
        listener();
    }

    return () => listeners.delete(listener);
};
